// @flow
import React from 'react';
import {
  compose,
  setDisplayName,
  withProps,
  defaultProps,
  withState,
  withHandlers,
  toClass,
} from 'recompose';
import reactCSS from 'reactcss';
import { FormattedMessage } from 'react-intl';
import CascadingFormattedMessage from '@ux/cascading-formatted-message';
import { StaticSearch, StickySearch } from './search';
// eslint-disable-next-line import/no-named-as-default
import ExactMatch from './exact-match';
import DomainList from './DomainList';
import AfternicInBasket from './negotiate/AddedToBasket';
// eslint-disable-next-line import/no-named-as-default
import Filters from './filters';
import { WubLoader } from '@ux/penders';
import {
  Heading,
  H,
} from '@team-griffin/react-heading-section';
import { Row, Column, Direction, Align } from '@ux/grid';
import ControlPanelIcon from '@ux/control-panel-icon';
import { Paragraph } from '@ux/dom';
import { withBreakpoints } from '@ux/responsive';
import { getStyles } from '@ux/style-utils';
import { gu2px } from '@ux/theme-utils';
import { headingS } from '@ux/type-size';
import longhand from '@team-griffin/css-longhand';
import cssSides, { Side } from '@team-griffin/css-sides';
import * as r from 'ramda';
import * as rA from 'ramda-adjunct';
import Transition from 'react-inline-transition-group';
import { Watch } from 'scrollmonitor-react';
import { whenTrue, ifElseBool, notEquals } from '../../crosscutting/capra';

export const stylesheet = ({
  theme,
}) => {
  const {
    palette,
    typography,
    spacing,
  } = theme;

  return {
    default: {
      root: {
      },
      info: {
        ...cssSides('padding', Side.Y, gu2px(3, spacing)),
        ...cssSides('padding', Side.X, gu2px(6, spacing)),
        textAlign: 'center',
        marginTop: gu2px(3, spacing),
      },
      infoTitle: {
        ...headingS(typography),
      },
      infoIcon: {
        ...longhand('margin', {
          left: 'auto',
          right: 'auto',
          bottom: gu2px(2, spacing),
        }),
      },
      meta: {
        marginTop: gu2px(3, spacing),
      },
      resultsText: {
        marginTop: gu2px(2, spacing),
        marginBottom: gu2px(1, spacing),
        textAlign: 'center',
        opacity: 0.3,
        transition: 'opacity 0.3s ease-in-out',
      },
      resultsTitle: {
        ...headingS(typography),
        marginBottom: gu2px(1, spacing),
        color: palette.grey02,
        fontWeight: 600,
        textAlign: 'center',
      },
      domainTerm: {
        marginTop: gu2px(1, spacing),
        textAlign: 'center',
      },
      loader: {
        root: {
          marginLeft: 'auto',
          marginRight: 'auto',
          marginBottom: gu2px(3, spacing),
        },
        circle1: {
          backgroundColor: palette.primary,
        },
        circle2: {
          backgroundColor: palette.primary,
        },
      },
    },

    showResults: {
      resultsText: {
        opacity: 1,
      },
    },

    desktop: {
      meta: {
        marginTop: gu2px(4, spacing),
        marginBottom: gu2px(1, spacing),
        ...cssSides('margin', Side.X, gu2px(4, spacing)),
      },
      filters: {
        marginBottom: gu2px(2, spacing),
      },
      resultsText: {
        ...cssSides('margin', Side.Y, gu2px(1, spacing)),
        textAlign: 'left',
      },
      resultsTitle: {
        textAlign: 'left',
      },
      domainTerm: {
        textAlign: 'left',
      },
    },
  };
};

const animationStyles = {
  base: {
    opacity: 0,
  },
  appear: {
    opacity: 1,
    transition: 'opacity 600ms ease-in-out',
  },
  enter: {
    opacity: 1,
    transition: 'opacity 600ms ease-in-out',
  },
  leave: {
    opacity: 0,
    transition: 'opacity 300ms ease-in-out',
  },
};

export const stylesheetToStyles = (stylesheet, props) => reactCSS(stylesheet, {
  desktop: props.isMinLg,
  showResults: props.showResults,
});

const PureNoResults = ({
  theme,
  styles,
  showExactMatch,
}) => (
  <Transition childrenStyles={animationStyles}>
    <div>
      <div style={styles.info}>
        <div style={styles.infoIcon}>
          <ControlPanelIcon
            theme={theme}
            icon="magnifyingGlass"
            size={gu2px(3, theme.spacing)}
          />
        </div>
        <Heading
          component={(
            <H style={styles.infoTitle}>
              {ifElseBool(
                r.always(
                  <FormattedMessage
                    id="dst.noResultsHasExact.title"
                  />
                ),
                r.always(
                  <FormattedMessage id="dst.noResults.title"/>
                )
              )(showExactMatch)}
            </H>
          )}
        />
        <Paragraph theme={theme}>
          <FormattedMessage id="dst.noResults.text"/>
        </Paragraph>
      </div>
    </div>
  </Transition>
);

const NoResults = compose(
  setDisplayName('NoResults'),
)(PureNoResults);

const PureEmpty = ({
  theme,
  styles,
}) => (
  <Transition childrenStyles={animationStyles}>
    <div>
      <div style={styles.info}>
        <div style={styles.infoIcon}>
          <ControlPanelIcon
            theme={theme}
            icon="magnifyingGlass"
          />
        </div>
        <Heading
          component={(
            <H style={styles.infoTitle}>
              <FormattedMessage
                id="dst.empty.title"
              />
            </H>
          )}
        />
        <Paragraph theme={theme}>
          <FormattedMessage id="dst.empty.text"/>
        </Paragraph>
      </div>
    </div>
  </Transition>
);

const Empty = compose(
  setDisplayName('Empty'),
)(PureEmpty);

const PureError = ({
  theme,
  styles,
}) => (
  <Transition childrenStyles={animationStyles}>
    <div>
      <div style={styles.info}>
        <div style={styles.infoIcon}>
          <ControlPanelIcon
            theme={theme}
            icon="circleExclamation"
          />
        </div>
        <Heading
          component={(
            <H style={styles.infoTitle}>
              <FormattedMessage id="dst.error.title"/>
            </H>
          )}
        />
        <Paragraph theme={theme}>
          <FormattedMessage id="dst.error.text"/>
        </Paragraph>
      </div>
    </div>
  </Transition>
);

export const Error = compose(
  setDisplayName('Error'),
)(PureError);

// eslint-disable-next-line
const WatchedSearch = Watch(toClass(StaticSearch));

export const PureDomainSuggestionTool = ({
  bundleOptions,
  features,
  tlds,
  query,
  results,
  domains,
  exactMatch,
  fqdn,
  theme,
  currency,
  isMinSm,
  isMinMd,
  isMinLg,
  showEmpty,
  showExactMatch,
  showContent,
  showSearching,
  showFiltering,
  showResults,
  showFilters,
  showNoResults,
  showAfternicInBasket,
  showError,
  styles,
  sticky,
  stickyOffset,
  legal,
  canUnspin,
  metaRowDirection,
  initialFilters,
  includeTax,
  usePPE,
  taxRate,
  // Events
  onPurchaseDomainClick,
  onPurchaseBundleClick,
  onTransferDomainClick,
  onBackorderDomainClick,
  onNegotiateDomainClick,
  onStaticSearchFormSubmit,
  onStaticSearchFormChange,
  onStickySearchFormSubmit,
  onStickySearchFormChange,
  onFilterFormChange,
  onScroll,
  onViewBasketClick,
}: Object) => (
  <div style={styles.root}>
    <WatchedSearch
      stateChange={onScroll}
      isMinSm={isMinSm}
      isMinMd={isMinMd}
      initialValues={{
        query,
      }}
      theme={theme}
      onSubmit={onStaticSearchFormSubmit}
      onChange={onStaticSearchFormChange}
      onViewBasketClick={onViewBasketClick}
    />

    <StickySearch
      theme={theme}
      offset={stickyOffset}
      isMinSm={isMinSm}
      isMinMd={isMinMd}
      sticky={sticky}
      onSubmit={onStickySearchFormSubmit}
      onChange={onStickySearchFormChange}
      onViewBasketClick={onViewBasketClick}
      initialValues={{
        query,
      }}
    />
    {whenTrue(
      r.always(
        <div>
          {whenTrue(
            r.always(
              <Transition childrenStyles={animationStyles}>
                <div>
                  <ExactMatch
                    theme={theme}
                    fqdn={fqdn}
                    availability={exactMatch.availability}
                    price={exactMatch.price}
                    oldPrice={exactMatch.oldPrice}
                    legalMessages={exactMatch.legalMessages}
                    currency={currency}
                    isMinMd={isMinMd}
                    isMinLg={isMinLg}
                    canTransfer={exactMatch.canTransfer}
                    canBackorder={exactMatch.canBackorder}
                    canNegotiate={exactMatch.canNegotiate}
                    status={results.exactMatchDomainStatus}
                    features={features}
                    bundleDomains={exactMatch.bundles}
                    bundleOptions={bundleOptions}
                    bundleAvailability={results.bundleAvailability}
                    onPurchaseDomainClick={onPurchaseDomainClick}
                    onPurchaseBundleClick={onPurchaseBundleClick}
                    onTransferDomainClick={onTransferDomainClick}
                    onBackorderDomainClick={onBackorderDomainClick}
                    onNegotiateDomainClick={onNegotiateDomainClick}
                    onViewBasketClick={onViewBasketClick}
                  />
                </div>
              </Transition>
            ),
            showExactMatch,
          )}

          {whenTrue(
            () => (
              <Transition childrenStyles={animationStyles}>
                <div>
                  <AfternicInBasket
                    theme={theme}
                    fqdn={exactMatch.fqdn}
                    canTransfer={exactMatch.canTransfer}
                    onViewBasketClick={onViewBasketClick}
                  />
                </div>
              </Transition>
            ),
            showAfternicInBasket,
          )}

          <div style={styles.meta}>
            <Row
              theme={theme}
              direction={metaRowDirection}
              align={Align.CENTER}
            >
              <Column
                theme={theme}
                xs={12}
                lg={(canUnspin) ? 12 : 6}
              >
                <div style={styles.filters}>
                  <Transition childrenStyles={animationStyles}>
                    <Filters
                      key="filters"
                      showFilters={showFilters}
                      theme={theme}
                      features={features}
                      resultsCount={r.length(domains)}
                      onSubmit={onFilterFormChange}
                      initialFilters={initialFilters}
                      tlds={tlds}
                      currency={currency}
                      isMinMd={isMinMd}
                      isMinLg={isMinLg}
                    />
                  </Transition>
                </div>
              </Column>
              <Column
                theme={theme}
                xs={12}
                lg={(canUnspin) ? 12 : 6}
              >
                <Transition childrenStyles={animationStyles}>
                  {whenTrue(
                    () => (
                      <div key="text">
                        <div style={styles.resultsText}>
                          <Heading
                            component={(
                              <H style={styles.resultsTitle}>
                                <FormattedMessage
                                  id="dst.standardDomains.title"
                                  values={{
                                    count: domains.length,
                                  }}
                                />
                              </H>
                            )}
                          />
                          <div style={styles.domainTerm}>
                            <Paragraph theme={theme}>
                              <CascadingFormattedMessage
                                cascade={[
                                  {
                                    id: r.cond([
                                      [
                                        r.always(usePPE && includeTax),
                                        // eslint-disable-next-line max-len
                                        r.always('dst.standardDomains.includesTax'),
                                      ],
                                      [
                                        r.always(usePPE && r.not(includeTax)),
                                        // eslint-disable-next-line max-len
                                        r.always('dst.standardDomains.excludesTax'),
                                      ],
                                      [ r.T, r.always('') ],
                                    ])(null),
                                    values: {
                                      taxRate,
                                    },
                                  },
                                  {
                                    id: 'dst.standardDomains.taxation',
                                  },
                                  {
                                    id: 'dst.standardDomains.domainTerm',
                                  },
                                ]}
                              />
                            </Paragraph>
                          </div>
                        </div>
                      </div>
                    ),
                    showFilters,
                  )}
                </Transition>
              </Column>
            </Row>
          </div>

          {whenTrue(
            () => (
              <Transition childrenStyles={animationStyles}>
                <div style={styles.info}>
                  <WubLoader
                    size={40}
                    style={styles.loader}
                  />
                  <Heading
                    component={(
                      <H style={styles.infoTitle}>
                        <FormattedMessage id="dst.searching.title"/>
                      </H>
                    )}
                  />
                  <Paragraph theme={theme}>
                    <FormattedMessage id="dst.searching.text"/>
                  </Paragraph>
                </div>
              </Transition>
            ),
            showSearching,
          )}

          {whenTrue(
            () => (
              <Transition childrenStyles={animationStyles}>
                <div style={styles.info}>
                  <WubLoader
                    size={40}
                    style={styles.loader}
                  />
                </div>
              </Transition>
            ),
            showFiltering,
          )}

          {whenTrue(
            () => (
              <NoResults
                theme={theme}
                styles={styles}
                showExactMatch={showExactMatch}
              />
            ),
            showNoResults,
          )}

          {whenTrue(
            () => (
              <Error
                theme={theme}
                styles={styles}
              />
            ),
            showError,
          )}

          {whenTrue(
            () => (
              <div>
                <Transition childrenStyles={animationStyles}>
                  <div>
                    <DomainList
                      theme={theme}
                      domains={domains}
                      currency={currency}
                      isMinSm={isMinSm}
                      isMinMd={isMinMd}
                      isMinLg={isMinLg}
                      onViewBasketClick={onViewBasketClick}
                      onPurchaseDomainClick={onPurchaseDomainClick}
                      onBackorderDomainClick={onBackorderDomainClick}
                      onTransferDomainClick={onTransferDomainClick}
                      onNegotiateDomainClick={onNegotiateDomainClick}
                      canBackorder={features.backorder}
                      canTransfer={features.transfer}
                      canNegotiate={features.negotiate}
                      legal={legal}
                    />
                  </div>
                </Transition>
              </div>
            ),
            showResults,
          )}
        </div>
      ),
      showContent,
    )}
    {whenTrue(
      () => (
        <Empty
          theme={theme}
          styles={styles}
        />
      ),
      showEmpty,
    )}
  </div>
);

export const enhance = compose(
  setDisplayName('DomainSuggestionTool'),
  defaultProps({
    exactMatch: {
    },
    domains: [],
    //@TODO investigate moving this default feature set to somewhere more
    // applicable
    features: {
      premium: true,
      backorder: true,
      transfer: true,
      canUnspin: false,
      filters: true,
      negotiate: false,
    },
  }),
  withBreakpoints,
  withState('sticky', 'setSticky', false),
  withProps((ownerProps) => {
    const {
      searching,
      filtering,
      query,
      errored,
      hasSearched,
      features,
      results,
      afternicInBasket,
    } = ownerProps;

    const exactMatch = r.prop('exactMatch', results);
    const domains = r.prop('standard', results);
    const canUnspin = r.pathEq([ 'features', 'canUnspin' ], true)(ownerProps);

    const anyResults = r.pipe(
      r.length,
      r.gt(r.__, 0)
    )(domains);
    const fqdn = r.propOr(query, 'fqdn', exactMatch);

    const showResults = r.allPass([
      () => r.equals(filtering, false),
      () => r.equals(searching, false),
      () => r.equals(anyResults, true),
      () => r.equals(errored, false),
    ])();

    // reverse flex direction if large screen and can't unspin
    const metaRowDirection = r.ifElse(
      () => r.and(ownerProps.isMinLg, r.not(canUnspin)),
      r.always(Direction.ROW_REVERSE),
      r.always(Direction.ROW),
    )();

    return {
      domains,
      exactMatch,
      fqdn,
      canUnspin,
      metaRowDirection,
      showResults,
      styles: getStyles(stylesheet, stylesheetToStyles, {
        ...ownerProps,
        showResults,
      }),
      showContent: rA.isNotNil(query),
      showEmpty: r.and(
        r.equals(hasSearched, false),
        rA.isNilOrEmpty(query),
      ),
      showExactMatch: r.allPass([
        () => r.equals(hasSearched, true),
        () => rA.isNotEmpty(exactMatch),
        () => r.equals(errored, false),
        () => notEquals(afternicInBasket, true),
      ])(),
      showAfternicInBasket: r.equals(afternicInBasket, true),
      showFilters: r.and(
        r.pathOr(true, [ 'filters' ], features),
        r.or(
          r.allPass([
            () => rA.isNotNil(query),
            () => r.equals(hasSearched, true),
            () => r.equals(searching, false),
          ])(),
          r.equals(filtering, true),
        ),
      ),
      showSearching: r.and(
        r.equals(searching, true),
        r.equals(filtering, false),
      ),
      showFiltering: r.and(
        r.equals(searching, true),
        r.equals(filtering, true)
      ),
      showNoResults: r.allPass([
        () => r.equals(searching, false),
        () => r.equals(anyResults, false),
        () => r.equals(errored, false),
        () => r.equals(hasSearched, true),
      ])(),
      showError: r.and(
        r.equals(searching, false),
        r.equals(errored, true)
      ),
    };
  }),
  withHandlers({
    onScroll: ({
      setSticky,
    }) => (watcher) => setSticky(watcher.isAboveViewport),
  }),
);

export default enhance(PureDomainSuggestionTool);
